/**
 * Tenant Admin Hooks
 * React Query hooks for tenant-scoped admin operations
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@services/queryClient";
import * as tenantAdminService from "@services/tenantAdmin";
import type {
  TenantStats,
  TenantSettingsResponse,
  TenantSettingsUpdate,
  TenantGuide,
  TenantGuideListItem,
  TenantGuideCreate,
  TenantGuideUpdate,
  TenantLibraryResource,
  TenantLibraryResourceListItem,
  TenantLibraryResourceCreate,
  TenantLibraryResourceUpdate,
  TenantAgentTemplate,
  TenantAgentTemplateListItem,
  TenantAgentTemplateCreate,
  TenantAgentTemplateUpdate,
} from "@/types/tenantAdmin";

// ============================================================================
// Stats Hook
// ============================================================================

export function useTenantStats() {
  return useQuery<TenantStats, Error>({
    queryKey: queryKeys.tenantAdmin.stats(),
    queryFn: tenantAdminService.getTenantStats,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  });
}

// ============================================================================
// Settings Hooks
// ============================================================================

export function useTenantSettings() {
  return useQuery<TenantSettingsResponse, Error>({
    queryKey: queryKeys.tenantAdmin.settings(),
    queryFn: tenantAdminService.getTenantSettings,
  });
}

export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();

  return useMutation<TenantSettingsResponse, Error, TenantSettingsUpdate>({
    mutationFn: tenantAdminService.updateTenantSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.settings(),
      });
    },
  });
}

// ============================================================================
// Guides Hooks
// ============================================================================

export function useTenantGuides() {
  return useQuery<TenantGuideListItem[], Error>({
    queryKey: queryKeys.tenantAdmin.guides.list(),
    queryFn: tenantAdminService.getTenantGuides,
  });
}

export function useTenantGuide(slug: string | undefined) {
  return useQuery<TenantGuide, Error>({
    queryKey: queryKeys.tenantAdmin.guides.detail(slug || ""),
    queryFn: () => tenantAdminService.getTenantGuide(slug!),
    enabled: !!slug,
  });
}

export function useCreateTenantGuide() {
  const queryClient = useQueryClient();

  return useMutation<TenantGuide, Error, TenantGuideCreate>({
    mutationFn: tenantAdminService.createTenantGuide,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.guides.all(),
      });
    },
  });
}

export function useUpdateTenantGuide() {
  const queryClient = useQueryClient();

  return useMutation<
    TenantGuide,
    Error,
    { slug: string; data: TenantGuideUpdate }
  >({
    mutationFn: ({ slug, data }) =>
      tenantAdminService.updateTenantGuide(slug, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.guides.detail(variables.slug),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.guides.all(),
      });
    },
  });
}

export function useDeleteTenantGuide() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: tenantAdminService.deleteTenantGuide,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.guides.all(),
      });
    },
  });
}

// ============================================================================
// Library Resource Hooks
// ============================================================================

export function useTenantLibraryResources() {
  return useQuery<TenantLibraryResourceListItem[], Error>({
    queryKey: queryKeys.tenantAdmin.library.list(),
    queryFn: tenantAdminService.getTenantLibraryResources,
  });
}

export function useTenantLibraryResource(id: string | undefined) {
  return useQuery<TenantLibraryResource, Error>({
    queryKey: queryKeys.tenantAdmin.library.detail(id || ""),
    queryFn: () => tenantAdminService.getTenantLibraryResource(id!),
    enabled: !!id,
  });
}

export function useCreateTenantLibraryResource() {
  const queryClient = useQueryClient();

  return useMutation<TenantLibraryResource, Error, TenantLibraryResourceCreate>(
    {
      mutationFn: tenantAdminService.createTenantLibraryResource,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tenantAdmin.library.all(),
        });
      },
    },
  );
}

export function useUpdateTenantLibraryResource() {
  const queryClient = useQueryClient();

  return useMutation<
    TenantLibraryResource,
    Error,
    { id: string; data: TenantLibraryResourceUpdate }
  >({
    mutationFn: ({ id, data }) =>
      tenantAdminService.updateTenantLibraryResource(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.library.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.library.all(),
      });
    },
  });
}

export function useDeleteTenantLibraryResource() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: tenantAdminService.deleteTenantLibraryResource,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.library.all(),
      });
    },
  });
}

// ============================================================================
// Agent Template Hooks
// ============================================================================

export function useTenantAgentTemplates() {
  return useQuery<TenantAgentTemplateListItem[], Error>({
    queryKey: queryKeys.tenantAdmin.templates.list(),
    queryFn: tenantAdminService.getTenantAgentTemplates,
  });
}

export function useTenantAgentTemplate(id: string | undefined) {
  return useQuery<TenantAgentTemplate, Error>({
    queryKey: queryKeys.tenantAdmin.templates.detail(id || ""),
    queryFn: () => tenantAdminService.getTenantAgentTemplate(id!),
    enabled: !!id,
  });
}

export function useCreateTenantAgentTemplate() {
  const queryClient = useQueryClient();

  return useMutation<TenantAgentTemplate, Error, TenantAgentTemplateCreate>({
    mutationFn: tenantAdminService.createTenantAgentTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.templates.all(),
      });
    },
  });
}

export function useUpdateTenantAgentTemplate() {
  const queryClient = useQueryClient();

  return useMutation<
    TenantAgentTemplate,
    Error,
    { id: string; data: TenantAgentTemplateUpdate }
  >({
    mutationFn: ({ id, data }) =>
      tenantAdminService.updateTenantAgentTemplate(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.templates.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.templates.all(),
      });
    },
  });
}

export function useDeleteTenantAgentTemplate() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: tenantAdminService.deleteTenantAgentTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.tenantAdmin.templates.all(),
      });
    },
  });
}
